import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import FilterComponent, { FilterOption, AppliedFilter } from '../../../components/common/FilterComponent';
import PaginationComponent from '../../../components/common/PaginationComponent';
import { entityService, Entity } from '../../../services/entityService';

interface PaginationInfo {
    page: number;
    size: number;
    total: number;
    totalPages: number;
}

const EntityAdminPage: React.FC = () => {
    const [entities, setEntities] = useState<Entity[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const history = useHistory();
    const [showEntityModal, setShowEntityModal] = useState<boolean>(false);
    const [showAddEntityModal, setShowAddEntityModal] = useState<boolean>(false);
    const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
    const [formData, setFormData] = useState<Partial<Entity>>({});
    const [showFilter, setShowFilter] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState<'name' | 'description' | 'status'>('name');
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

    // Pagination state
    const [pagination, setPagination] = useState<PaginationInfo>({
        page: 0,
        size: 10,
        total: 0,
        totalPages: 0
    });

    const filterOptions: FilterOption[] = [
        { value: 'name', label: 'Nome' },
        { value: 'description', label: 'Descrição' },
        { value: 'status', label: 'Status' }
    ];

    const fetchData = async (filters?: { nome?: string; descricao?: string; status?: string }, page?: number, size?: number) => {
        try {
            setLoading(true);

            // Use entityService to fetch data
            const response = await entityService.getEntities(filters, page, size);
            const entitiesData = response.content || response.data || response || [];
            setEntities(entitiesData);

            // Update pagination info
            const currentPage = page !== undefined ? page : pagination.page;
            const currentSize = size !== undefined ? size : pagination.size;
            setPagination(prev => ({
                ...prev,
                page: currentPage,
                size: currentSize,
                total: response.totalElements || response.total || 0,
                totalPages: response.totalPages || Math.ceil((response.totalElements || response.total || 0) / currentSize)
            }));

            setError(null);
        } catch (err) {
            setError('Failed to fetch data. Please try again later.');
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Function to apply filters (called when user clicks search or presses Enter)
    const handleFilterApply = (filterText: string, filterType: string) => {
        const typedFilterType = filterType as 'name' | 'description' | 'status';

        // Only apply if filter text is not empty
        if (!filterText.trim()) return;

        // Add to applied filters
        const filterOption = filterOptions.find(opt => opt.value === typedFilterType);
        const newAppliedFilter: AppliedFilter = {
            type: typedFilterType,
            value: filterText.trim(),
            label: filterOption?.label || typedFilterType
        };

        // Remove existing filter of same type if exists
        const updatedFilters = appliedFilters.filter(f => f.type !== typedFilterType);
        setAppliedFilters([...updatedFilters, newAppliedFilter]);

        // Clear the input after applying
        setFilterText('');

        // Apply the filter to backend
        applyFiltersToBackend([...updatedFilters, newAppliedFilter]);
    };

    // Function to remove a specific filter
    const handleFilterRemove = (filterType: string) => {
        const updatedFilters = appliedFilters.filter(f => f.type !== filterType);
        setAppliedFilters(updatedFilters);
        applyFiltersToBackend(updatedFilters);
    };

    // Function to apply filters to backend
    const applyFiltersToBackend = (filters: AppliedFilter[]) => {
        // Reset to first page when filtering
        const newPage = 0;
        setPagination(prev => ({ ...prev, page: newPage }));

        if (filters.length > 0) {
            // Map frontend filter types to backend field names
            const filterMap: Record<string, string> = {
                'name': 'nome',
                'description': 'descricao',
                'status': 'status'
            };

            const backendFilters: any = {};
            filters.forEach(filter => {
                const backendField = filterMap[filter.type];
                backendFilters[backendField] = filter.value;
            });

            fetchData(backendFilters, newPage, pagination.size);
        } else {
            // If no filters, fetch all data
            fetchData(undefined, newPage, pagination.size);
        }
    };

    // Function to clear filters
    const handleFilterClear = () => {
        setFilterText('');
        setAppliedFilters([]);
        const newPage = 0;
        setPagination(prev => ({ ...prev, page: newPage }));
        fetchData(undefined, newPage, pagination.size);
    };

    // Function to handle page change
    const handlePageChange = (newPage: number) => {
        setPagination(prev => ({ ...prev, page: newPage }));

        // Build filters for API call from applied filters
        let filters: any = undefined;
        if (appliedFilters.length > 0) {
            const filterMap: Record<string, string> = {
                'name': 'nome',
                'description': 'descricao',
                'status': 'status'
            };
            filters = {};
            appliedFilters.forEach(filter => {
                const backendField = filterMap[filter.type];
                filters[backendField] = filter.value;
            });
        }

        fetchData(filters, newPage, pagination.size);
    };

    // Function to handle page size change
    const handlePageSizeChange = (newSize: number) => {
        const newPage = 0; // Reset to first page when changing page size
        setPagination(prev => ({ ...prev, page: newPage, size: newSize }));

        // Build filters for API call from applied filters
        let filters: any = undefined;
        if (appliedFilters.length > 0) {
            const filterMap: Record<string, string> = {
                'name': 'nome',
                'description': 'descricao',
                'status': 'status'
            };
            filters = {};
            appliedFilters.forEach(filter => {
                const backendField = filterMap[filter.type];
                filters[backendField] = filter.value;
            });
        }

        fetchData(filters, newPage, newSize);
    };

    const handleAddEntityClick = () => {
        setFormData({
            nome: '',
            descricao: '',
            status: 'ATIVO'
        });
        setShowAddEntityModal(true);
    };

    const handleEditEntityClick = (entity: Entity) => {
        setSelectedEntity(entity);
        setFormData(entity);
        setShowEntityModal(true);
    };

    const handleEntityModalClose = () => {
        setShowEntityModal(false);
        setShowAddEntityModal(false);
        setSelectedEntity(null);
        setFormData({});
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        // Handle number inputs (like codIbge)
        if (name === 'codIbge') {
            // Limit to 7 digits maximum
            if (value.length > 7) {
                return; // Don't update if more than 7 digits
            }
            const numValue = value === '' ? undefined : parseInt(value, 10);
            setFormData(prev => ({
                ...prev,
                [name]: numValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmitEntity = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (showAddEntityModal) {
                // Add new entity via entityService
                const newEntityData = {
                    nome: formData.nome || '',
                    descricao: formData.descricao || '',
                    status: formData.status || 'INATIVO',
                    codIbge: formData.codIbge
                };
                const response = await entityService.createEntity(newEntityData);

                // Add the new entity to the list (assuming the response contains the created entity)
                setEntities(prevEntities => [...prevEntities, response]);
            } else if (selectedEntity) {
                // Update existing entity via entityService
                const updateEntityData = {
                    nome: formData.nome || '',
                    descricao: formData.descricao || '',
                    status: formData.status || 'INATIVO',
                    codIbge: formData.codIbge
                };

                const response = await entityService.updateEntity(selectedEntity.entidade_id, updateEntityData);

                // Update the entity in the list with the response data
                setEntities(prevEntities =>
                    prevEntities.map(entity =>
                        entity.entidade_id === selectedEntity.entidade_id
                            ? response
                            : entity
                    )
                );
            }

            handleEntityModalClose();
        } catch (err) {
            setError('Failed to save entity. Please try again later.');
            console.error('Error saving entity:', err);
        }
    };

    if (loading) {
        return <div className="text-center p-5">Loading...</div>;
    }

    return (
        <div className="container-fluid p-4">
            <Button
                variant="outline-secondary"
                onClick={() => history.push('/admin')}
                className="return-button mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Gerenciamento de Entidades</h2>
                <Button variant="primary" onClick={handleAddEntityClick}>
                    Adicionar Entidade
                </Button>
            </div>

            {!showFilter && (
                <div className="mb-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowFilter(true)}
                        className="filter-toggle-button d-inline-flex align-items-center gap-2"
                        aria-expanded={showFilter}
                    >
                        <span>🔍</span>
                        Filtrar
                    </Button>
                </div>
            )}
            {showFilter && (
                <div className="mb-3">
                    <FilterComponent
                        showFilter={showFilter}
                        filterText={filterText}
                        filterType={filterType}
                        filterOptions={filterOptions}
                        appliedFilters={appliedFilters}
                        onFilterTypeChange={(type) => setFilterType(type as 'name' | 'description' | 'status')}
                        onFilterTextChange={setFilterText}
                        onFilterTextClear={handleFilterClear}
                        onFilterToggle={() => setShowFilter(false)}
                        onFilterApply={handleFilterApply}
                        onFilterRemove={handleFilterRemove}
                        placeholderText={`Buscar por ${filterType === 'name' ? 'nome' : filterType === 'description' ? 'descrição' : 'status'}...`}
                    />
                </div>
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Descrição</th>
                            <th>Cód. IBGE</th>
                            <th>Status</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entities.map(entity => (
                            <tr key={entity.entidade_id}>
                                <td>{entity.nome}</td>
                                <td>{entity.descricao}</td>
                                <td>{entity.codIbge}</td>
                                <td>
                                    <span className={`badge ${entity.status === 'ATIVO' ? 'bg-success' : 'bg-danger'}`}>
                                        {entity.status}
                                    </span>
                                </td>
                                <td>
                                    <Button
                                        variant="outline-secondary"
                                        className="d-flex align-items-center border border-secondary text-secondary"
                                        size="sm"
                                        onClick={() => handleEditEntityClick(entity)}
                                    >
                                        Editar Entidade
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Pagination Controls */}
            <PaginationComponent
                page={pagination.page}
                size={pagination.size}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                entityLabel="entidades"
            />

            {/* Entity Edit Modal */}
            <Modal show={showEntityModal} onHide={handleEntityModalClose}>
                <Modal.Header>
                    <Modal.Title>Editar Entidade</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitEntity}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nome</Form.Label>
                            <Form.Control
                                type="text"
                                name="nome"
                                value={formData.nome || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Descrição</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="descricao"
                                value={formData.descricao || ''}
                                onChange={handleInputChange}
                                rows={3}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Control
                                as="select"
                                name="status"
                                value={formData.status || ''}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="ATIVO">Ativo</option>
                                <option value="INATIVO">Inativo</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Cód. IBGE</Form.Label>
                            <Form.Control
                                type="number"
                                name="codIbge"
                                value={formData.codIbge || ''}
                                onChange={handleInputChange}
                                max="9999999"
                                maxLength={7}
                                minLength={7}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleEntityModalClose}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            Salvar Alterações
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Add Entity Modal */}
            <Modal show={showAddEntityModal} onHide={handleEntityModalClose}>
                <Modal.Header>
                    <Modal.Title>Adicionar Entidade</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitEntity}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nome</Form.Label>
                            <Form.Control
                                type="text"
                                name="nome"
                                value={formData.nome || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Descrição</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="descricao"
                                value={formData.descricao || ''}
                                onChange={handleInputChange}
                                rows={3}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Control
                                as="select"
                                name="status"
                                value={formData.status || 'ATIVO'}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="ATIVO">Ativo</option>
                                <option value="INATIVO">Inativo</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Cód. IBGE</Form.Label>
                            <Form.Control
                                type="number"
                                name="codIbge"
                                value={formData.codIbge || ''}
                                onChange={handleInputChange}
                                max="9999999"
                                maxLength={7}
                                minLength={7}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleEntityModalClose}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            Adicionar Entidade
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default EntityAdminPage;
